import { useState } from "react";
import { X, ShoppingBag, Gift } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FreeShippingProgress } from "./FreeShippingProgress";
import { CouponInput } from "./CouponInput";
import { BonusProducts } from "./BonusProducts";
import { GiftRewards } from "./GiftRewards";
import { CartItem } from "./CartItem";
import { GiftCartItem } from "./GiftCartItem";
import { giftTiers, bonusProducts as giftBonusProducts } from "@/data/giftData";
import { useGifts } from "@/contexts/GiftContext";
import { toast } from "sonner";

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

// Demo data
const initialCartItems = [
  {
    id: "1",
    name: "T-Shirt Demo 1",
    variant: "Black, Unisex, XL",
    price: 9.99,
    originalPrice: undefined as number | undefined,
    quantity: 2,
    image: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=200&h=200&fit=crop",
  },
];

const bonusProducts = giftBonusProducts;

export const CartDrawer = ({ isOpen, onClose }: CartDrawerProps) => {
  const [cartItems, setCartItems] = useState(initialCartItems);
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [discount, setDiscount] = useState<string | null>(null);
  const { selectedGifts, selectedVariants, selectGift, getChosenGifts } = useGifts();

  // Calculate discount percentage from applied coupon
  const discountPercentage = appliedCoupon ? 10 : 0; // 10% for valid coupons
  
  const subtotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  
  const discountAmount = (subtotal * discountPercentage) / 100;

  const freeShippingThreshold = 49;

  const giftSubtotal = subtotal - discountAmount;
  const chosenGifts = getChosenGifts(giftSubtotal);

  const handleUpdateQuantity = (id: string, quantity: number) => {
    const item = cartItems.find(i => i.id === id);
    setCartItems((items) =>
      items.map((item) =>
        item.id === id ? { ...item, quantity } : item
      )
    );
    if (item) {
      toast.success("Množstvo aktualizované", {
        description: `${item.name}: ${quantity} ks`,
      });
    }
  };

  const handleRemoveItem = (id: string) => {
    const item = cartItems.find(i => i.id === id);
    setCartItems((items) => items.filter((item) => item.id !== id));
    if (item) {
      toast.info("Produkt odstránený", {
        description: item.name,
      });
    }
  };

  const handleApplyCoupon = async (code: string): Promise<boolean> => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 800));
    
    if (code === "ZLAVA10" || code === "WELCOME") {
      setAppliedCoupon(code);
      setDiscount("-10%");
      toast.success("Zľavový kód aplikovaný!", {
        description: `Kód ${code} - zľava 10%`,
      });
      return true;
    }
    toast.error("Neplatný zľavový kód", {
      description: "Skontrolujte správnosť kódu a skúste znova.",
    });
    return false;
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setDiscount(null);
    toast.info("Zľavový kód odstránený");
  };

  const handleAddBonusProduct = (productId: string, variant?: string) => {
    const product = bonusProducts.find((p) => p.id === productId);
    if (product) {
      setCartItems((items) => [
        ...items,
        {
          id: variant ? `${product.id}-${variant}` : product.id,
          name: product.name,
          variant: variant ?? "Bonus produkt",
          price: product.discountedPrice,
          originalPrice: product.originalPrice,
          quantity: 1,
          image: product.image,
        },
      ]);
      toast.success("Bonus produkt pridaný!", {
        description: product.name,
      });
    }
  };

  const itemCount = cartItems.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-foreground/40 backdrop-blur-sm z-[200] transition-opacity duration-300 ${
          isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div
        className={`fixed right-0 top-0 h-full w-full sm:max-w-md bg-background shadow-2xl z-[210] transform transition-transform duration-300 ease-out ${
          isOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-semibold text-foreground">
                Váš košík
              </h2>
              <span className="w-6 h-6 rounded-full bg-badge text-badge-foreground text-xs font-semibold flex items-center justify-center">
                {itemCount}
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto">
            {/* Free Shipping Progress */}
            <div className="p-4 border-b border-border">
              <FreeShippingProgress
                currentAmount={subtotal}
                freeShippingThreshold={freeShippingThreshold}
              />
            </div>

            {/* Cart Items */}
            <div className="border-b border-border">
              {cartItems.length === 0 ? (
                <div className="py-12 text-center">
                  <ShoppingBag className="w-12 h-12 text-muted-foreground/50 mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    Váš košík je prázdny
                  </p>
                </div>
              ) : (
                <>
                  {cartItems.map((item) => (
                    <CartItem
                      key={item.id}
                      {...item}
                      discountPercentage={discountPercentage}
                      onUpdateQuantity={handleUpdateQuantity}
                      onRemove={handleRemoveItem}
                    />
                  ))}
                  {chosenGifts.map((gift) => (
                    <div key={`gift-${gift.tierId}`} className="p-3">
                      <GiftCartItem
                        name={gift.name}
                        image={gift.image}
                        variant={gift.selectedVariant}
                        originalPrice={gift.value}
                        onRemove={() => selectGift(gift.tierId, gift.id, gift.selectedVariant)}
                      />
                    </div>
                  ))}
                </>
              )}
            </div>

            {/* Coupon Input - Only show if cart has items */}
            {cartItems.length > 0 && (
              <div className="p-4 border-b border-border">
                <CouponInput
                  onApply={handleApplyCoupon}
                  appliedCoupon={appliedCoupon}
                  discount={discount || undefined}
                  onRemove={handleRemoveCoupon}
                />
              </div>
            )}

            {/* Gifts - Only show if cart has items */}
            {cartItems.length > 0 && (
              <div className="p-4 border-b border-border">
                <GiftRewards
                  subtotal={giftSubtotal}
                  tiers={giftTiers}
                  selectedGifts={selectedGifts}
                  selectedVariants={selectedVariants}
                  onSelectGift={selectGift}
                  variant="compact"
                  maxPerTier={3}
                  showAllLink
                />
              </div>
            )}

            {/* Bonus Products - Only show if cart has items */}
            {cartItems.length > 0 && (
              <div className="p-4">
                <BonusProducts
                  products={bonusProducts}
                  maxDisplay={3}
                  onAddProduct={handleAddBonusProduct}
                />
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="border-t border-border p-4 bg-background">
            <div className="mb-4">
              <div className="flex justify-between items-center mb-1">
                <span className="text-sm text-muted-foreground">Medzisúčet:</span>
                {discountPercentage > 0 ? (
                  <div className="text-right">
                    <span className="text-sm text-muted-foreground line-through mr-2">
                      {subtotal.toFixed(2).replace(".", ",")} €
                    </span>
                    <span className="text-xl font-bold text-foreground">
                      {(subtotal - discountAmount).toFixed(2).replace(".", ",")} €
                    </span>
                  </div>
                ) : (
                  <span className="text-xl font-bold text-foreground">
                    {subtotal.toFixed(2).replace(".", ",")} €
                  </span>
                )}
              </div>
              {discount && (
                <div className="flex justify-between items-center text-sm">
                  <span className="text-primary">Zľava ({appliedCoupon}):</span>
                  <span className="text-primary font-medium">-{discountAmount.toFixed(2).replace(".", ",")} €</span>
                </div>
              )}
              {chosenGifts.length > 0 && (
                <div className="mt-2 space-y-1">
                  {chosenGifts.map((gift) => (
                    <div key={gift.id} className="flex justify-between items-center text-sm">
                      <span className="flex items-center gap-1.5 text-foreground truncate">
                        <Gift className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                        <span className="truncate">{gift.name}</span>
                      </span>
                      <span className="text-primary font-semibold whitespace-nowrap">Zadarmo</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-xs text-muted-foreground mt-1">
                Platba a doprava sa vypočíta pri pokladni
              </p>
            </div>

            <div className="space-y-3">
              <a 
                href="/pokladna"
                onClick={onClose}
                className="flex items-center justify-center w-full h-14 rounded-xl text-base font-bold transition-all duration-300 hover:scale-[1.02] hover:shadow-xl hover:shadow-primary/30 active:scale-[0.98] group bg-gradient-to-r from-primary to-primary/90 hover:from-primary/90 hover:to-primary text-primary-foreground"
              >
                <span>Pokračovať k pokladni</span>
                <span className="ml-2 transition-transform duration-300 group-hover:translate-x-1.5">→</span>
              </a>
              <a
                href="/kosik"
                onClick={onClose}
                className="flex items-center justify-center w-full h-12 rounded-xl text-base font-medium transition-all duration-300 hover:scale-[1.01] hover:bg-secondary border border-input hover:border-primary/50 active:scale-[0.99] bg-background text-foreground"
              >
                Zobraziť celý košík
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
